"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  Users,
  Heart,
  Calendar,
  CreditCard,
  FileText,
  BarChart3,
  Settings,
  ArrowRight,
  Loader2,
  Clock,
  X,
  Zap,
  FlaskConical,
  Pill,
  PlusCircle,
  ClipboardList,
  LogIn,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  group: string;
  href?: string;
  action?: () => void;
  keywords?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function speciesEmoji(species: string): string {
  const s = species?.toLowerCase() || "";
  if (s.includes("canine") || s.includes("dog")) return "\u{1F415}";
  if (s.includes("feline") || s.includes("cat")) return "\u{1F408}";
  if (s.includes("equine") || s.includes("horse")) return "\u{1F434}";
  return "\u{1F43E}";
}

/** Levenshtein distance (client-side; mirrors server implementation). */
function levenshtein(s1: string, s2: string): number {
  const a = s1.toLowerCase();
  const b = s2.toLowerCase();
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const row: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = i;
    for (let j = 1; j <= b.length; j++) {
      const val =
        a[i - 1] === b[j - 1]
          ? row[j - 1]
          : Math.min(row[j - 1], row[j], prev) + 1;
      row[j - 1] = prev;
      prev = val;
    }
    row[b.length] = prev;
  }
  return row[b.length];
}

/** Fuzzy-match query against target using token-level Levenshtein. */
function fuzzyMatch(query: string, target: string): boolean {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase().trim();
  if (!q || !t) return false;
  if (t.includes(q)) return true;
  const qTokens = q.split(/\s+/);
  const tTokens = t.split(/\s+/);
  return qTokens.every((qt) => {
    const tolerance = qt.length <= 3 ? 0 : qt.length <= 5 ? 1 : 2;
    return tTokens.some((tt) => levenshtein(qt, tt) <= tolerance);
  });
}

/** Filter static command items with fuzzy matching. */
function fuzzyFilterItems(items: CommandItem[], query: string): CommandItem[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.keywords?.some((k) => k.toLowerCase().includes(q)) ||
      fuzzyMatch(q, item.label) ||
      (item.description ? fuzzyMatch(q, item.description) : false) ||
      (item.keywords?.some((k) => fuzzyMatch(q, k)) ?? false)
  );
}

// ─── Recent searches (localStorage) ──────────────────────────────────────

const RECENT_SEARCHES_KEY = "nitra_recent_searches";
const MAX_RECENT_SEARCHES = 6;

function getRecentSearches(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query: string) {
  if (!query.trim()) return;
  try {
    const current = getRecentSearches();
    const updated = [
      query.trim(),
      ...current.filter((q) => q !== query.trim()),
    ].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

function removeRecentSearch(query: string) {
  try {
    const current = getRecentSearches();
    const updated = current.filter((q) => q !== query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

// ─── Default static navigation items ─────────────────────────────────────

const DEFAULT_ITEMS: CommandItem[] = [
  {
    id: "schedule",
    label: "Schedule",
    description: "View appointments",
    icon: <Calendar className="h-4 w-4" />,
    group: "Pages",
    href: "/schedule",
    keywords: ["appointments", "calendar"],
  },
  {
    id: "patients",
    label: "Patients",
    description: "Search patients",
    icon: <Heart className="h-4 w-4" />,
    group: "Pages",
    href: "/patients",
    keywords: ["pets", "animals"],
  },
  {
    id: "clients",
    label: "Clients",
    description: "Search clients",
    icon: <Users className="h-4 w-4" />,
    group: "Pages",
    href: "/clients",
    keywords: ["owners", "people"],
  },
  {
    id: "cards",
    label: "Cards",
    description: "Manage payment cards",
    icon: <CreditCard className="h-4 w-4" />,
    group: "Pages",
    href: "/cards",
    keywords: ["payment", "credit"],
  },
  {
    id: "billing",
    label: "Billing",
    description: "Invoices and payments",
    icon: <FileText className="h-4 w-4" />,
    group: "Pages",
    href: "/billing",
    keywords: ["invoices", "payments"],
  },
  {
    id: "reports",
    label: "Reports",
    description: "View analytics",
    icon: <BarChart3 className="h-4 w-4" />,
    group: "Pages",
    href: "/reports",
    keywords: ["analytics", "data"],
  },
  {
    id: "settings",
    label: "Settings",
    description: "Manage settings",
    icon: <Settings className="h-4 w-4" />,
    group: "Pages",
    href: "/settings",
    keywords: ["preferences", "config"],
  },
];

// Icon map for server-returned quick actions (keyed on action id prefix).
function quickActionIcon(id: string): ReactNode {
  if (id.includes("soap")) return <ClipboardList className="h-4 w-4 text-emerald-500" />;
  if (id.includes("schedule") || id.includes("appointment")) return <Calendar className="h-4 w-4 text-purple-500" />;
  if (id.includes("check-in") || id.includes("checkin")) return <LogIn className="h-4 w-4 text-sky-500" />;
  if (id.includes("invoice")) return <FileText className="h-4 w-4 text-amber-500" />;
  if (id.includes("prescription") || id.includes("rx")) return <Pill className="h-4 w-4 text-rose-500" />;
  if (id.includes("lab")) return <FlaskConical className="h-4 w-4 text-blue-500" />;
  if (id.includes("client")) return <Users className="h-4 w-4 text-blue-500" />;
  if (id.includes("patient")) return <Heart className="h-4 w-4 text-emerald-500" />;
  return <Zap className="h-4 w-4 text-slate-400" />;
}

// ─── API result interfaces ────────────────────────────────────────────────

interface AppointmentResult {
  id: string;
  scheduledStart: string;
  status: string;
  reason: string | null;
  patient: { id: string; name: string; species: string };
  client: { firstName: string; lastName: string };
  appointmentType: { name: string } | null;
}

interface QuickActionResult {
  id: string;
  label: string;
  description: string;
  href: string;
  keywords: string[];
}

interface ContextPatient {
  id: string;
  name: string;
  species: string;
  breed: string | null;
}

interface SearchResults {
  patients: Array<{
    id: string;
    name: string;
    species: string;
    breed: string | null;
    microchipNumber?: string | null;
    client: { firstName: string; lastName: string };
  }>;
  clients: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phonePrimary: string | null;
  }>;
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    total: number;
    status: string;
    clientId: string;
    client?: { firstName: string; lastName: string };
  }>;
  appointments: AppointmentResult[];
  quickActions?: QuickActionResult[];
  contextPatient?: ContextPatient | null;
}

// ─── Props ────────────────────────────────────────────────────────────────

export interface CommandPaletteProps {
  items?: CommandItem[];
  /** External open state — can be used to open from the header search bar. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When provided, smart context mode is activated:
   * - A context banner shows the current patient at the top.
   * - The API receives `patientId` to enable patient-scoped result hints.
   */
  contextPatientId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────

export function CommandPalette({
  items,
  open: externalOpen,
  onOpenChange,
  contextPatientId,
}: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Support both controlled (external) and uncontrolled (internal) open state.
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (onOpenChange) onOpenChange(value);
      else setInternalOpen(value);
    },
    [onOpenChange]
  );

  const allItems = items || DEFAULT_ITEMS;

  // Filter static page items using fuzzy matching.
  const filteredPages = query
    ? fuzzyFilterItems(allItems, query)
    : allItems;

  // ── Build dynamic items from API results ────────────────────────────

  // Quick actions from server (keyword-matched).
  const quickActionItems: CommandItem[] =
    searchResults?.quickActions?.map((qa) => ({
      id: qa.id,
      label: qa.label,
      description: qa.description,
      icon: quickActionIcon(qa.id),
      group: "Quick Actions",
      href: qa.href,
    })) ?? [];

  const patientItems: CommandItem[] =
    searchResults?.patients.map((p) => {
      const chipHint = p.microchipNumber ? ` · Chip: ${p.microchipNumber}` : "";
      return {
        id: `patient-${p.id}`,
        label: `${speciesEmoji(p.species)} ${p.name}`,
        description: `${p.species}${p.breed ? ` · ${p.breed}` : ""}${chipHint} · Owner: ${p.client.firstName} ${p.client.lastName}`,
        icon: <Heart className="h-4 w-4 text-emerald-500" />,
        group: "Patients",
        href: `/patients/${p.id}`,
      };
    }) ?? [];

  const clientItems: CommandItem[] =
    searchResults?.clients.map((c) => ({
      id: `client-${c.id}`,
      label: `${c.firstName} ${c.lastName}`,
      description: c.email || c.phonePrimary || "",
      icon: <Users className="h-4 w-4 text-blue-500" />,
      group: "Clients",
      href: `/clients/${c.id}`,
    })) ?? [];

  const invoiceItems: CommandItem[] =
    searchResults?.invoices.map((inv) => ({
      id: `invoice-${inv.id}`,
      label: inv.invoiceNumber,
      description: `$${Number(inv.total).toFixed(2)} · ${inv.status}${inv.client ? ` · ${inv.client.firstName} ${inv.client.lastName}` : ""}`,
      icon: <FileText className="h-4 w-4 text-amber-500" />,
      group: "Invoices",
      href: `/billing/${inv.id}`,
    })) ?? [];

  const appointmentItems: CommandItem[] =
    searchResults?.appointments.map((appt) => {
      const date = new Date(appt.scheduledStart);
      const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      return {
        id: `appointment-${appt.id}`,
        label: `${speciesEmoji(appt.patient.species)} ${appt.patient.name}`,
        description: `${appt.appointmentType?.name ?? "Appointment"} · ${dateStr} ${timeStr} · ${appt.client.firstName} ${appt.client.lastName}`,
        icon: <Calendar className="h-4 w-4 text-purple-500" />,
        group: "Appointments",
        href: `/schedule?appointmentId=${appt.id}`,
      };
    }) ?? [];

  // ── Combine items: Quick Actions first, then pages, then API results ─
  const combinedItems: CommandItem[] =
    searchResults !== null
      ? [
          ...quickActionItems,
          ...filteredPages,
          ...patientItems,
          ...clientItems,
          ...invoiceItems,
          ...appointmentItems,
        ]
      : filteredPages;

  // Group results.
  const grouped = combinedItems.reduce<Record<string, CommandItem[]>>(
    (acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
    },
    {}
  );

  const flatFiltered = Object.values(grouped).flat();

  const execute = useCallback(
    (item: CommandItem) => {
      if (query.trim().length >= 2) {
        saveRecentSearch(query.trim());
      }
      setOpen(false);
      setQuery("");
      setSearchResults(null);
      if (item.href) router.push(item.href);
      else item.action?.();
    },
    [router, query, setOpen]
  );

  // ── Debounced API search ─────────────────────────────────────────────
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query.trim() });
        if (contextPatientId) params.set("patientId", contextPatientId);
        const res = await fetch(`/api/search?${params.toString()}`);
        if (!res.ok) throw new Error("Search failed");
        const raw = await res.json();
        const data: SearchResults = raw.data ?? raw;
        setSearchResults(data);
      } catch {
        setSearchResults(null);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [query, contextPatientId]);

  // ── Keyboard shortcut to open (Cmd+K / Ctrl+K) ──────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, setOpen]);

  // ── Focus input when opened; load recent searches ────────────────────
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setSearchResults(null);
      setRecentSearches(getRecentSearches());
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // ── Keyboard navigation ──────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => (i < flatFiltered.length - 1 ? i + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => (i > 0 ? i - 1 : flatFiltered.length - 1));
      } else if (e.key === "Enter" && flatFiltered[selectedIndex]) {
        e.preventDefault();
        execute(flatFiltered[selectedIndex]);
      }
    },
    [flatFiltered, selectedIndex, execute, setOpen]
  );

  // ── Scroll selected item into view ───────────────────────────────────
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // ── Reset selection on filter change ────────────────────────────────
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  const showRecentSearches = !query.trim() && recentSearches.length > 0;
  const contextPatient = searchResults?.contextPatient;

  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="combobox"
        aria-expanded={open}
        aria-controls="command-palette-listbox"
        onKeyDown={handleKeyDown}
      >
        {/* Smart context banner */}
        {contextPatientId && (
          <div className="flex items-center gap-2 border-b border-slate-100 bg-emerald-50 px-4 py-2 rounded-t-2xl">
            <Heart className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span className="text-xs text-emerald-700">
              Context:{" "}
              <span className="font-medium">
                {contextPatient
                  ? `${speciesEmoji(contextPatient.species)} ${contextPatient.name}`
                  : "Loading…"}
              </span>
              {contextPatient?.breed ? ` · ${contextPatient.breed}` : ""}
            </span>
            <span className="ml-auto text-[10px] text-emerald-500">Patient scope active</span>
          </div>
        )}

        {/* Search input */}
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <Search className="h-5 w-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              contextPatientId
                ? "Search this patient's records, or type an action…"
                : "Search patients, clients, phone, chip, or type 'new soap'…"
            }
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            aria-label="Global search"
            role="searchbox"
          />
          {searching && (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400 shrink-0" />
          )}
          <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-400 sm:inline">
            ESC
          </kbd>
        </div>

        {/* Recent searches (shown when query is empty) */}
        {showRecentSearches && (
          <div className="border-b border-slate-100 p-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-1.5">
              Recent Searches
            </div>
            {recentSearches.map((recent) => (
              <div
                key={recent}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-50 group"
              >
                <Clock className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                <button
                  type="button"
                  className="flex-1 text-left text-sm text-slate-600 hover:text-slate-900"
                  onClick={() => setQuery(recent)}
                >
                  {recent}
                </button>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200"
                  aria-label={`Remove "${recent}" from recent searches`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecentSearch(recent);
                    setRecentSearches(getRecentSearches());
                  }}
                >
                  <X className="h-3 w-3 text-slate-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Quick action hints shown when query is empty */}
        {!query.trim() && !showRecentSearches && (
          <div className="border-b border-slate-100 p-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-1.5">
              Quick Tips
            </div>
            <div className="grid grid-cols-2 gap-1 px-3 py-1">
              {[
                { label: "new soap", hint: "Create SOAP note" },
                { label: "check in", hint: "Open check-in board" },
                { label: "555-0123", hint: "Search by phone" },
                { label: "schedule", hint: "Open scheduler" },
              ].map(({ label, hint }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setQuery(label)}
                  className="flex items-center gap-1.5 text-left rounded-md px-2 py-1 hover:bg-slate-50 text-xs text-slate-500"
                >
                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] text-slate-400 font-mono">
                    {label}
                  </kbd>
                  <span className="text-slate-400">{hint}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results list */}
        <div
          ref={listRef}
          id="command-palette-listbox"
          role="listbox"
          className="max-h-72 overflow-auto p-2"
        >
          {flatFiltered.length === 0 && !searching && query.trim().length >= 2 && (
            <div className="py-6 text-center text-sm text-slate-400">
              No results found for &lsquo;{query}&rsquo;
            </div>
          )}
          {flatFiltered.length === 0 && searching && (
            <div className="py-6 text-center text-sm text-slate-400">
              Searching…
            </div>
          )}
          {flatFiltered.length === 0 && !searching && !query.trim() && !showRecentSearches && (
            <div className="py-6 text-center text-sm text-slate-400">
              Start typing to search
            </div>
          )}
          {Object.entries(grouped).map(([group, groupItems]) => (
            <div key={group}>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5">
                {group === "Quick Actions" && (
                  <Zap className="h-3 w-3 text-amber-400" />
                )}
                {group}
              </div>
              {groupItems.map((item) => {
                const idx = flatIndex++;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-index={idx}
                    onClick={() => execute(item)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-left transition-colors",
                      idx === selectedIndex
                        ? "bg-emerald-50 text-emerald-700"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span
                      className={cn(
                        "shrink-0",
                        idx === selectedIndex
                          ? "text-emerald-600"
                          : "text-slate-400"
                      )}
                    >
                      {item.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{item.label}</div>
                      {item.description && (
                        <div className="truncate text-xs text-slate-400">
                          {item.description}
                        </div>
                      )}
                    </div>
                    {idx === selectedIndex && (
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer — keyboard shortcuts legend */}
        <div className="border-t border-slate-100 px-4 py-2 rounded-b-2xl">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
            <span>
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                ↑↓
              </kbd>{" "}
              Navigate
            </span>
            <span>
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                ↵
              </kbd>{" "}
              Select
            </span>
            <span>
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                Esc
              </kbd>{" "}
              Close
            </span>
            <span>
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                ⌘K
              </kbd>{" "}
              Toggle
            </span>
            <span className="ml-auto flex items-center gap-1">
              <PlusCircle className="h-2.5 w-2.5" />
              Type
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                new soap
              </kbd>
              or
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                check in
              </kbd>
              for quick actions
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
